import { Component, OnInit, OnDestroy } from '@angular/core'
import { Lead, LeadReasonLabels } from '@parkupp/core'
import { LeadsService } from '../../../services/leads.service'
import { Subscription } from 'rxjs'
import { Router } from '@angular/router'

@Component({
    selector: 'app-admin-list-parking-leads',
    templateUrl: './admin-list-parking-leads.component.html',
    styleUrls: ['./admin-list-parking-leads.component.scss'],
})
export class AdminListParkingLeadsComponent implements OnInit, OnDestroy {
    leads: Lead[] = []
    filteredLeads: Lead[] = []
    loading = true
    searchTerm = ''
    selectedReason = ''
    leadReasonLabels = LeadReasonLabels

    private leadsSubscription: Subscription

    constructor(private leadsService: LeadsService, private router: Router) {}

    ngOnInit(): void {
        this.loadLeads()
    }

    ngOnDestroy(): void {
        if (this.leadsSubscription) {
            this.leadsSubscription.unsubscribe()
        }
    }

    loadLeads(): void {
        this.loading = true
        this.leadsSubscription = this.leadsService.subscribeToLeads().subscribe({
            next: (leads) => {
                this.leads = leads
                this.applyFilters()
                this.loading = false
            },
            error: (error) => {
                console.error('Error loading leads:', error)
                this.loading = false
            },
        })
    }

    applyFilters(): void {
        this.filteredLeads = this.leads.filter((lead) => {
            const matchesSearch =
                !this.searchTerm ||
                lead.name.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                lead.surname.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                lead.email.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                lead.idealAddress.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
                lead.city.toLowerCase().includes(this.searchTerm.toLowerCase())

            const matchesReason = !this.selectedReason || lead.reasonForNotFinding === this.selectedReason

            return matchesSearch && matchesReason
        })
    }

    onSearchChange(): void {
        this.applyFilters()
    }

    onReasonFilterChange(): void {
        this.applyFilters()
    }

    clearFilters(): void {
        this.searchTerm = ''
        this.selectedReason = ''
        this.applyFilters()
    }

    viewOnMap(): void {
        this.router.navigate(['/admin/parking-leads-map'])
    }

    formatDate(date: Date): string {
        return new Date(date).toLocaleDateString('en-ZA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    getReasonKeys(): (keyof typeof LeadReasonLabels)[] {
        return Object.keys(this.leadReasonLabels) as (keyof typeof LeadReasonLabels)[]
    }

    getReasonLabel(reasonKey: keyof typeof LeadReasonLabels): string {
        return this.leadReasonLabels[reasonKey]
    }

    trackByLeadId(index: number, lead: Lead): string {
        return lead.$key || index.toString()
    }

    deleteLead(lead: Lead): void {
        if (confirm(`Are you sure you want to delete the lead from ${lead.name} ${lead.surname}?`)) {
            this.leadsService
                .deleteLead(lead.$key!)
                .then(() => {
                    // Lead will be automatically removed from the list due to the subscription
                })
                .catch((error) => {
                    console.error('Error deleting lead:', error)
                    alert('Error deleting lead. Please try again.')
                })
        }
    }

    exportLeads(): void {
        const csvContent = this.generateCSV()
        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `parking-leads-export-${new Date().toISOString().split('T')[0]}.csv`
        link.click()
        window.URL.revokeObjectURL(url)
    }

    private generateCSV(): string {
        const headers = ['Date Created', 'Name', 'Surname', 'Email', 'Phone', 'Ideal Address', 'City', 'Province', 'Country', 'Reason', 'Comments']

        const rows = this.filteredLeads.map((lead) => [
            this.formatDate(lead.createdAt),
            lead.name,
            lead.surname,
            lead.email,
            lead.phoneNumber,
            lead.idealAddress,
            lead.city,
            lead.province,
            lead.country,
            this.leadReasonLabels[lead.reasonForNotFinding],
            lead.additionalComments || '',
        ])

        const csvContent = [headers, ...rows].map((row) => row.map((field) => `"${field}"`).join(',')).join('\n')

        return csvContent
    }
}
